import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    CircularProgress,
    Stack,
    Typography
} from '@mui/material';
import { SwapHoriz } from '@mui/icons-material';
import { CompanyDTO, CompanyResourceApi } from '@root/api';
import { navigateToState } from '@root/app/shared/navigation/navigation.service';
import { authService } from '@root/app/shared/new-services/auth.service';
import { companyService } from '@root/app/shared/new-services/company.service';
import { errorService } from '@root/app/shared/new-services/newError.service';
import { useTranslation } from '@root/app/shared/hooks/useTranslation';
import { HF_ICON_SIZES } from '@root/app/shared/theme/designTokens';
import HfCompanySelectorModal from './HfCompanySelectorModal';

const companyApi = new CompanyResourceApi();
const INCOGNITO_IMAGE = 'images/avengers-logo.jpg';
const PLACEHOLDER_IMAGE = 'images/placeholder_enterprise.png';

const resetCompanyCaches = (): void => {
    companyService.resetUserCompanyCache();
};

/** Full page reload is required after company switch (legacy AngularJS + cached state). */
const reloadAfterCompanyChange = (): void => {
    navigateToState('private.dashboard');
    window.location.reload();
};

const HfCompanySelector: React.FC = () => {
    const { t } = useTranslation();
    const [companyDetails, setCompanyDetails] = useState<CompanyDTO | null>(null);
    const [hasMultipleCompanies, setHasMultipleCompanies] = useState(false);
    const [changingCompany, setChangingCompany] = useState(false);
    const [incognitoMode, setIncognitoMode] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const companyImage = useMemo(() => {
        if (incognitoMode) {
            return INCOGNITO_IMAGE;
        }
        if (!companyDetails?.imageUrl) {
            return PLACEHOLDER_IMAGE;
        }
        return companyDetails.imageUrl;
    }, [companyDetails, incognitoMode]);

    const companyDisplayName = useMemo(() => {
        if (incognitoMode) {
            return 'Avengers';
        }
        return companyDetails?.name || t('UNKNOWN_NAME');
    }, [companyDetails, incognitoMode, t]);

    const canChangeCompany = authService.isADVISOR() || hasMultipleCompanies;

    const checkMultipleCompanies = useCallback(() => {
        companyService.getAllCompanies((err, data) => {
            if (err || !data) {
                return;
            }
            const page = data as { result?: CompanyDTO[]; total?: number };
            const total = page.total ?? (page.result?.length ?? 0);
            setHasMultipleCompanies(total > 1);
        }, undefined, 0, 2);
    }, []);

    const changeCompany = useCallback((companyId: string) => {
        if (!companyId || companyId === companyDetails?.id) {
            return;
        }

        setChangingCompany(true);

        companyApi.changeCompany(companyId)
            .then((response) => {
                resetCompanyCaches();
                authService.storeCompanyUser(response.data);
                authService.me((err) => {
                    if (err) {
                        errorService.reportError(err);
                    }
                    reloadAfterCompanyChange();
                });
            })
            .catch((err) => {
                setChangingCompany(false);
                errorService.reportError(err);
            });
    }, [companyDetails?.id]);

    useEffect(() => {
        if (authService.isADVISOR()) {
            try {
                const advisors = JSON.parse(localStorage.getItem('advisors') || 'null');
                if (advisors?.incognitoMode) {
                    setIncognitoMode(true);
                }
            } catch {
                setIncognitoMode(false);
            }
            setHasMultipleCompanies(true);
        } else {
            checkMultipleCompanies();
        }

        companyService.getUserCompany((err, company) => {
            if (err) {
                errorService.reportError(err);
                return;
            }
            setCompanyDetails(company ?? null);
        });
    }, [checkMultipleCompanies]);

    const handleOpenModal = () => {
        if (!canChangeCompany || changingCompany) {
            return;
        }
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        if (changingCompany) {
            return;
        }
        setModalOpen(false);
    };

    const handleSelectCompany = (company: CompanyDTO) => {
        setModalOpen(false);
        if (company.id) {
            changeCompany(company.id);
        }
    };

    return (
        <>
            <Stack
                id='company_container'
                direction='column'
                alignItems='center'
                sx={{ width: '100%' }}
            >
                <Box
                    component='img'
                    id='company_image'
                    alt='company'
                    src={companyImage}
                    sx={(theme) => ({
                        display: 'block',
                        width: 60,
                        height: 60,
                        bgcolor: theme.palette.grey[100],
                        borderRadius: 1,
                        mb: 1.25
                    })}
                />
                {canChangeCompany ? (
                    <Stack
                        id='company_name_container'
                        direction='row'
                        spacing={0.25}
                        alignItems='center'
                        justifyContent='center'
                        onClick={handleOpenModal}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleOpenModal();
                            }
                        }}
                        role='button'
                        tabIndex={0}
                        aria-haspopup='dialog'
                        aria-expanded={modalOpen}
                        sx={{
                            cursor: changingCompany ? 'wait' : 'pointer',
                            width: '100%',
                            mt: 0.5,
                            opacity: changingCompany ? 0.7 : 1,
                            overflow: 'hidden'
                        }}
                    >
                        <Typography
                            id='company_name'
                            component='span'
                            variant='body3'
                            sx={(theme) => ({
                                color: theme.palette.hfLayout.paper,
                                textAlign: 'center',
                                lineHeight: 1.3,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                            })}
                        >
                            {companyDisplayName}
                        </Typography>
                        {changingCompany ? (
                            <CircularProgress size={14} sx={(theme) => ({ color: theme.palette.hfLayout.navText })} />
                        ) : (
                            <SwapHoriz sx={(theme) => ({ color: theme.palette.hfLayout.navText, fontSize: HF_ICON_SIZES.md })} />
                        )}
                    </Stack>
                ) : (
                    <Box id='company_name_container' sx={{ width: '100%', mt: 0.5 }}>
                        <Typography
                            id='company_name'
                            component='span'
                            variant='caption'
                            color='text.secondary'
                            sx={{ display: 'block', textAlign: 'center' }}
                        >
                            {companyDisplayName}
                        </Typography>
                    </Box>
                )}
            </Stack>

            <HfCompanySelectorModal
                open={modalOpen}
                currentCompanyId={companyDetails?.id}
                changingCompany={changingCompany}
                onClose={handleCloseModal}
                onSelectCompany={handleSelectCompany}
            />
        </>
    );
};

export default HfCompanySelector;
